import React from 'react'
import { Typography, theme } from 'antd'
import { fontSize, fontWeight } from '@vacationtracker/shared/constants/ThemeSetting'

const { Text: AntText } = Typography

export type TextTone = 'default' | 'secondary' | 'tertiary' | 'danger' | 'success' | 'warning'
export type TextSize = 'sm' | 'base' | 'lg'
export type TextWeight = 'regular' | 'medium' | 'bold'

export interface TextProps {
  /** Semantic color. Danger is a tone value, not a boolean modifier. Default: 'default'. */
  tone?: TextTone
  size?: TextSize
  weight?: TextWeight
  /** Renders as a block-level element instead of antd Text's default inline span. */
  block?: boolean
  children: React.ReactNode
  style?: React.CSSProperties
  /** Escape hatch for callers with existing CSS keyed off a class (e.g. responsive truncation rules). */
  className?: string
}

const TONE_TO_ANT_TYPE: Partial<Record<TextTone, 'secondary' | 'success' | 'warning' | 'danger'>> = {
  secondary: 'secondary',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
}

/**
 * Vacation Tracker's single body-text component. Wraps antd `Typography.Text`.
 * `tertiary` has no antd `type` equivalent, so its color is read straight from the
 * live theme token (`colorTextTertiary`) instead of an invented hex value.
 */
export function Text({ tone = 'default', size = 'base', weight = 'regular', block = false, children, style, className }: TextProps) {
  const { token } = theme.useToken()

  return (
    <AntText
      className={className}
      type={TONE_TO_ANT_TYPE[tone]}
      style={{
        fontSize: fontSize[size],
        fontWeight: fontWeight[weight],
        display: block ? 'block' : undefined,
        color: tone === 'tertiary' ? token.colorTextTertiary : undefined,
        ...style,
      }}
    >
      {children}
    </AntText>
  )
}

export default Text
